import React, { useState, useContext, createContext, useEffect, useRef, useMemo, useCallback } from "react";

// APIs
import api from "../services/api";
import { useAuth } from "./useAuth";
import { publish } from "../utils/events";
import { parseTranscribeNotification } from "../utils/transcribeNotification";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export const jobContext = createContext();

// Job types that produce a generated note. When one of these jobs disappears from
// the jobs list, we publish `generationCompleted` and clear `isGenerating`. Backend
// reality (per backend process audit): voice/telemed agents reuse the standard
// `Note - Hub` process and emit the same job-type prefix as standard notes — there
// is no separate "Agent"/"VoiceAgent" prefix. Don't widen this Set without
// confirming a real new prefix in the backend; broadening it produces false
// positives on unrelated jobs.
const NOTE_GENERATION_JOB_TYPES = new Set(["NewNote", "Note"]);

function useJobProvider() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [jobsMap, setJobsMap] = useState({});
  const [generatingMap, setGeneratingMap] = useState(() => {
    // Restore generating state from localStorage on initial load (survives hard refresh)
    let stored = {};
    try {
      let raw = localStorage.getItem("generatingMap");
      if (raw) {
        let parsed = JSON.parse(raw);
        // Discard entries older than 15 minutes
        let now = Date.now();
        for (let chatId of Object.keys(parsed)) {
          if (parsed[chatId].ts && now - parsed[chatId].ts < 15 * 60 * 1000) {
            stored[chatId] = parsed[chatId].userId || parsed[chatId];
          }
        }
      }
    } catch (e) {
      localStorage.removeItem("generatingMap");
    }
    return stored;
  });
  const { auth, logout, isServerAvailable, hostName } = useAuth();
  const pingIntervalRef = useRef();
  const controllerEventSourceRef = useRef();
  const sessionExpiredRef = useRef(false);
  const reconnectTimerRef = useRef();
  const [userActions, setUserActions] = useState([]);

  // The onerror reconnect below fires from a timer that outlives the render it
  // was scheduled in, so it cannot read `isServerAvailable` from a closure.
  // Mirror it into a ref that the reconnect path checks.
  const serverAvailableRef = useRef(isServerAvailable);
  useEffect(() => {
    serverAvailableRef.current = isServerAvailable;
  }, [isServerAvailable]);

  // isServerAvailable, not isOnline: the SSE stream and the ping fallback both
  // target the main api host, so during an outage they are doomed. It is in the
  // deps so the stream is re-established when the server comes back -- with
  // isOnline (which was never a dep) the connection had no re-arm at all.
  //
  // Because this effect now re-runs on every outage transition, its cleanup has
  // to tear down EVERYTHING the run started, not just the EventSource: a pending
  // 3s reconnect timer left armed would re-dial through the whole outage (the
  // churn the dep exists to stop) and, worse, fire after the next recovery has
  // already opened a fresh stream -- overwriting the ref and orphaning the live
  // one. The ping interval is in the same position: without clearing it, every
  // recovery stacked another 2s poll.
  useEffect(() => {
    if (auth?.user?.id && isServerAvailable) {
      sessionExpiredRef.current = false;
      // `typeof EventSource` is a non-empty string even when EventSource does not
      // exist ("undefined"), so this was always truthy and the startPing fallback
      // was dead code -- and in an EventSource-less shell connectSSE would throw.
      if (typeof EventSource !== "undefined") {
        connectSSE();
      } else {
        console.warn("SSE not supported");
        startPing();
      }
    }

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = undefined;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = undefined;
      }
      if (controllerEventSourceRef.current) {
        console.log("Closing SSE");
        controllerEventSourceRef.current?.close();
        controllerEventSourceRef.current = undefined;
      }
    };
    // eslint-disable-next-line
  }, [hostName, auth?.user?.id, isServerAvailable]);

  const connectSSE = useCallback(() => {
    if (sessionExpiredRef.current || !serverAvailableRef.current) return;
    getPing();
    // Bind every handler to THIS EventSource instance rather than to whatever
    // controllerEventSourceRef happens to point at when the handler runs. A
    // stale source's onerror firing after a newer stream was opened would
    // otherwise close the live one through the shared ref.
    const source = new EventSource(hostName + "/sse", { withCredentials: true });
    controllerEventSourceRef.current = source;

    source.onopen = function () {
      console.log("SSE connected");
      sessionExpiredRef.current = false;
      getPing();
    };

    source.onclose = function () {
      console.log("SSE closed");
    };

    source.onmessage = function (event) {
      let data = JSON.parse(event.data);

      if (data?.userActions && data.userActions.length > 0) {
        setUserActions((userActions) => {
          let newActions = [...userActions, ...data.userActions];
          return newActions;
        });
        toast.info(t("hooks.useJobs.newRequestReceived"));
        publish("newUserActions-" + data.userActions[0].chatId, data.userActions[0]);
      }

      if (data?.notifications) {
        checkNotifications(data?.notifications);
      }

      if (data?.clientCommands) {
        checkSessionMessages(data?.clientCommands);
      }
    };

    source.onerror = function (event) {
      console.warn("SSE ERROR ", event);
      // Identity check BEFORE anything else. Closing the right object was only
      // half the problem: a superseded source's onerror also ARMED a reconnect,
      // and the classic shape of that is the pre-outage stream erroring shortly
      // after recovery has already dialed a fresh one -- the timer then opens a
      // SECOND live stream and every SSE event is handled twice.
      const isCurrentSource = controllerEventSourceRef.current === source;
      // Close the source this handler belongs to, not the ref -- they diverge
      // once a newer stream has been opened.
      source.close();
      if (!isCurrentSource) {
        console.log("Ignoring SSE error from a superseded stream");
        return;
      }
      controllerEventSourceRef.current = undefined;

      if (sessionExpiredRef.current) return;
      // No point re-dialing into a known outage; the effect re-runs and
      // reconnects when isServerAvailable flips back.
      if (!serverAvailableRef.current) return;

      // Tracked so the effect cleanup can cancel it. Without this the timer
      // outlives its own effect run and re-dials during the outage anyway.
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = undefined;
        // A stream may have been established while this timer waited (the effect
        // re-runs on recovery and dials immediately). Dialing on top of it is the
        // same duplicate-stream bug from the other direction.
        if (controllerEventSourceRef.current) return;
        if (auth?.user?.id && !sessionExpiredRef.current && serverAvailableRef.current) {
          connectSSE();
        }
      }, 3000);
    };
    // eslint-disable-next-line
  }, [auth?.user?.id]);

  const startPing = () => {
    if (auth?.user?.id && isServerAvailable) {
      console.log("Start ping");
      pingIntervalRef.current = setInterval(getPing, 2000);
    }
  };

  useEffect(() => {
    let jobsMap = {};
    for (let job of jobs) {
      if (!jobsMap[job.chat]) {
        jobsMap[job.chat] = [];
      }

      let jobType = job.id.split(" ")[0].split("/")[1];
      if (NOTE_GENERATION_JOB_TYPES.has(jobType)) {
        setGenerating(job.chat, job.requesterUserId || auth?.user?.id);
      }

      jobsMap[job.chat].push(job);
    }

    setJobsMap(jobsMap);
    // eslint-disable-next-line
  }, [jobs]);

  const checkNotifications = (notifications) => {
    const parseOrderUpdateMetadataFromText = (rawText) => {
      if (typeof rawText !== "string") return null;

      const parsed = {};
      const data = {};

      if (rawText.includes("type=order-update")) {
        parsed.type = "order-update";
      }

      const idMatch = rawText.match(/(?:^|[,\s{])id=([^,}\n]+)/);
      if (idMatch?.[1]) {
        data.id = idMatch[1].trim();
      }

      const medicalNecessityMatch = rawText.match(/medicalNecessity=([\s\S]*?)(?:,\s*[A-Za-z0-9_.-]+=|\s*}$)/);
      if (medicalNecessityMatch?.[1]) {
        data.medicalNecessity = medicalNecessityMatch[1].trim();
      }

      if (!data.id && data.medicalNecessity) {
        const orderIdInText = data.medicalNecessity.match(/Order ID:\*\*\s*([0-9:]+)/i);
        if (orderIdInText?.[1]) {
          data.id = orderIdInText[1];
        }
      }

      if (Object.keys(data).length > 0) {
        parsed.data = data;
      }

      return Object.keys(parsed).length > 0 ? parsed : null;
    };

    const parseNotificationMetadata = (rawMetadata) => {
      if (!rawMetadata) return undefined;

      if (typeof rawMetadata === "object") {
        return rawMetadata;
      }

      if (typeof rawMetadata !== "string") {
        return undefined;
      }

      try {
        return JSON.parse(rawMetadata);
      } catch (e) {
        return parseOrderUpdateMetadataFromText(rawMetadata);
      }
    };

    const normalizeOrderUpdateMetadata = (rawMetadata) => {
      if (!rawMetadata || typeof rawMetadata !== "object") return rawMetadata;

      let data = rawMetadata.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          // Keep original shape if backend sends a plain string.
        }
      }

      if (!data || typeof data !== "object") {
        data = {};
      }

      if (rawMetadata.id && !data.id) {
        data.id = rawMetadata.id;
      }

      if (rawMetadata.medicalNecessity && !data.medicalNecessity) {
        data.medicalNecessity = rawMetadata.medicalNecessity;
      }

      return { ...rawMetadata, data };
    };

    for (let notification of notifications) {
      const metadata = parseNotificationMetadata(notification.metadata);
      const isOrderUpdateNotification =
        notification.title?.toLowerCase() === "order updated" || metadata?.type === "order-update";

      if (isOrderUpdateNotification) {
        console.log("[order-update] notification received", {
          title: notification.title,
          requesterUserId: notification.requesterUserId,
          metadataRaw: notification.metadata,
          metadataParsed: metadata,
        });
      }

      let key = typeof notification.metadata === "string" ? notification.metadata.split(" ") : undefined;
      let type = key?.[0];
      let chatId = key?.[1];
      if (metadata?.action === "Report Card Fix") {
        publish("reportCardFix", metadata);
      } else if (notification.title === "AVS Report") {
        setGenerating(chatId, false);
        publish("generationCompleted", { chatId, requesterUserId: notification.requesterUserId });
      } else if (notification.title === "Import encounters") {
        // Import Schedule
        let metadata = JSON.parse(notification.metadata);
        publish("importSchedule", metadata.data.encounters || []);
      } else if (notification.title === "Import Encounter") {
        // Import encounter
        let metadata = JSON.parse(notification.metadata);
        publish("importEncounter", { metadata });
      } else if (type === "created") {
        publish("encounterCreated", { chatId, requesterUserId: notification.requesterUserId });
      } else if (type === "updated") {
        publish("encounterUpdated", { chatId, requesterUserId: notification.requesterUserId });
      } else if (type === "assessment-start") {
        // Generation start
        setGenerating(chatId, notification.requesterUserId || auth?.user?.id);
      } else if (type === "assessment") {
        // Generation complete
        setGenerating(chatId, false);
        publish("generationCompleted", { chatId, requesterUserId: notification.requesterUserId });
      } else if (type === "transcribe") {
        // Transcription complete. Success/failure is decided by
        // parseTranscribeNotification — the notification's `type` is hardcoded
        // "success" server-side even for failures; the honest signal is the
        // 4th metadata token (see src/utils/transcribeNotification.js).
        console.log("Completed transcription notification with " + notification.type + " for " + chatId);
        const parsed = parseTranscribeNotification(notification);
        if (!parsed.error) {
          publish("transcriptionCompleted", { chatId: chatId, recordingId: parsed.recordingId });
        } else {
          publish("transcriptionCompleted", {
            chatId: chatId,
            recordingId: parsed.recordingId,
            error: parsed.error,
          });
        }
      }
      // Orders notifications
      else if (notification.title === "Order created") {
        publish("orderCreated", metadata);
      } else if (notification.title?.toLowerCase() === "order updated" || metadata?.type === "order-update") {
        const normalizedOrderUpdate = normalizeOrderUpdateMetadata(metadata);
        console.log("[order-update] publishing", {
          normalizedOrderUpdate,
        });
        publish("orderUpdated", normalizedOrderUpdate);
      } else if (notification.title === "Order List") {
        publish("ordersList", metadata);
      } else if (metadata?.type === "peptide-order-update") {
        // Peptide orders broadcast the full updated order (incl. new comments)
        // inside metadata.data. The peptide screens don't poll, so this is the
        // only live channel — publish it for PeptideOrderDetail/Fulfillment to
        // refetch when it matches the open order. metadata.data carries `@rid`
        // (not the REST `id`), so consumers match via peptideOrderUpdateMatchesId.
        publish("peptideOrderUpdated", normalizeOrderUpdateMetadata(metadata));
      } else {
        console.warn("Notification not handled", notification.metadata);
      }
    }
  };

  const checkSessionMessages = (clientCommands) => {
    for (let msg of clientCommands) {
      if (msg === "reload") {
        window.location.reload();
      } else {
        toast.info(msg);
      }
    }
  };

  const getPing = async () => {
    try {
      let data = await api.ping();

      if (data.status === 200) {
        updateJobs(data.data.session.jobs);
        setUserActions(data.data.session.userActions || []);
        if (data.data.session.clientCommands) checkSessionMessages(data.data.session.clientCommands);

        // Check notifications
        if (data.data.session.notifications && data.data.session.notifications.length > 0) {
          checkNotifications(data.data.session.notifications);
        }
      }
    } catch (e) {
      if (e.cause && e.cause.status === 403) {
        console.log("Session expired from job ping");
        sessionExpiredRef.current = true;
        if (controllerEventSourceRef.current) {
          controllerEventSourceRef.current.close();
        }
        logout();
      } else {
        console.warn("Error getting jobs", e);
      }
    }
  };

  const updateJobs = (jobs) => {
    setJobs((oldJobs) => {
      // After a refresh, oldJobs is empty. If server also returns no jobs but
      // generatingMap still has entries, those jobs completed while we were
      // disconnected — clear them and trigger refresh.
      if (oldJobs.length === 0 && jobs.length === 0) {
        let staleIds = Object.keys(generatingMap);
        if (staleIds.length > 0) {
          for (let chatId of staleIds) {
            publish("generationCompleted", { chatId, requesterUserId: generatingMap[chatId] });
            setGenerating(chatId, false);
          }
        }
        return oldJobs;
      }

      for (let job of oldJobs) {
        let found = false;
        for (let oldJob of jobs) {
          if (job.id === oldJob.id) {
            found = true;
            break;
          }
        }

        if (!found) {
          let jobType = job.id.split(" ")[0].split("/")[1];

          // End Job
          if (NOTE_GENERATION_JOB_TYPES.has(jobType)) {
            publish("generationCompleted", { chatId: job.chat, requesterUserId: job.requesterUserId });
            setGenerating(job.chat, false);
          } else if (jobType === "Transcript") {
            // toast.info(`Transcription completed`);
          } else if (jobType === "Compute") {
            // toast.info(`Transcription completed`);
          } else if (jobType === "Update") {
            // toast.info(`Transcription completed`);
          } else if (jobType === "AI") {
            // toast.info(`AI note updated`);
          } else if (jobType === "Import") {
            // toast.info(`AI note updated`);
          } else if (jobType === "Post") {
            // toast.info(`AI note updated`);
          } else {
            // toast.info(`Job completed: ${job.id}`);
          }
        }
      }

      if (JSON.stringify(oldJobs) !== JSON.stringify(jobs)) {
        return jobs;
      } else {
        return oldJobs;
      }
    });
  };

  const hasJobType = (chatId, jobType) => {
    if (!jobsMap[chatId]) return;

    jobsMap[chatId].find((job) => {
      let currentJobType = job.id.split(" ")[0].split("/")[1];
      return jobType === currentJobType;
    });
  };

  const isGenerating = (chatId) => {
    return generatingMap[chatId];
  };

  // Start time of each chat's in-flight full-note generation, so the recovery
  // timers in setGenerating can tell "still the same run" from "user re-generated".
  const generatingStartRef = useRef({});

  // Per-section regeneration state. Tracks which section variables on a given
  // chat are currently being regenerated so we can swap their bodies for a
  // section-level skeleton independently of the global isGenerating flag.
  // Shape: { [chatId]: { [variable]: { startedAt: number, beforeContent: any, mode, completing?, completedAt? } } }
  const [regeneratingMap, setRegeneratingMap] = useState({});
  // Mirror in a ref so async paths (refreshChat → completion handler) can read
  // the current state without going through stale closures.
  const regeneratingMapRef = useRef(regeneratingMap);
  useEffect(() => {
    regeneratingMapRef.current = regeneratingMap;
  }, [regeneratingMap]);

  const isRegenerating = (chatId, variable) => {
    return !!regeneratingMap[chatId]?.[variable];
  };

  const getRegenerating = (chatId, variable) => {
    return regeneratingMap[chatId]?.[variable];
  };

  // True when at least one section on this chat is currently regenerating.
  // Lets per-section UI distinguish "section regen lit isGenerating because
  // it shares the Note-Hub backend job" from "true full-note generation in
  // flight" — the former should NOT lock unrelated sections.
  const hasAnyRegenerating = (chatId) => {
    const entries = regeneratingMap[chatId];
    if (!entries) return false;
    return Object.keys(entries).length > 0;
  };

  // mode: "regenerate" (default) replaces the section body with a skeleton.
  // mode: "voiceEdit" keeps the existing body visible and renders only a thin
  // progress bar + status chip overlay so the user can verify their dictation
  // against existing content.
  const startRegenerating = (chatId, variable, beforeContent, mode = "regenerate", extras = {}) => {
    const startedAt = Date.now();
    // `extras.beforeRenderedHtml`: optional outerHTML/innerHTML snapshot of
    // what was on screen at click time. ChatNoteSection prefers this over the
    // raw `beforeContent` so the regen skeleton mirrors custom-rendered
    // structures (ICD-10 / Plan of Care / Problem List).
    setRegeneratingMap((prev) => ({
      ...prev,
      [chatId]: {
        ...(prev[chatId] || {}),
        [variable]: { startedAt, beforeContent, mode, beforeRenderedHtml: extras.beforeRenderedHtml || null },
      },
    }));

    // SSE recovery — fire one-shot checks at sanity thresholds (30s and 45s).
    // Real regens cluster at 10–15s; even a tier-3 model fallback cascade
    // (primary ~12s + secondary ~12s + tertiary ~12s ≈ 36s) shouldn't push
    // past 45s. If the entry is still active at those points the SSE was
    // likely dropped — publish "regenRecoveryCheck" so useChats does ONE
    // refreshChat to recover. Strictly bounded to two attempts.
    const recovery = (delay) => {
      setTimeout(() => {
        const entry = regeneratingMapRef.current[chatId]?.[variable];
        if (!entry || entry.completing || entry.aborted) return;
        if (entry.startedAt !== startedAt) return; // user re-started; ignore
        publish("regenRecoveryCheck", { chatId, variable });
      }, delay);
    };
    recovery(30000);
    recovery(45000);

    // Hard abort at 60s. By this point even the slowest legitimate retry
    // cascade should have responded — past 60s it really is broken. Show the
    // red "Please edit manually" flash, restore the user's original content,
    // and unmount. Honest UX: ~4× the median regen time as headroom, then
    // graceful failure instead of leaving the user staring at an indefinite
    // skeleton.
    setTimeout(() => {
      const entry = regeneratingMapRef.current[chatId]?.[variable];
      if (!entry || entry.completing || entry.aborted) return;
      if (entry.startedAt !== startedAt) return;
      markRegeneratingAborted(chatId, variable);
    }, 60000);
  };

  // Marks an entry as aborted. Triggers:
  // - A red "Please edit manually" flash (similar to the green "Done" flash but
  //   for failure) for 2 seconds, then unmount.
  // - Publishes "regenAborted" so useChats can restore the captured beforeContent
  //   to the section's contextVariable.
  // Used both for synchronous API failures (api.generate throws) and for
  // recovery-exhausted regens (90s timeout above).
  const markRegeneratingAborted = (chatId, variable) => {
    const entry = regeneratingMapRef.current[chatId]?.[variable];
    if (!entry || entry.completing || entry.aborted) return;
    const beforeContent = entry.beforeContent;
    const abortedAt = Date.now();

    setRegeneratingMap((prev) => {
      if (!prev[chatId]?.[variable]) return prev;
      const next = { ...prev, [chatId]: { ...prev[chatId] } };
      next[chatId][variable] = { ...next[chatId][variable], aborted: true, abortedAt };
      return next;
    });

    publish("regenAborted", { chatId, variable, beforeContent });

    // Auto-unmount after 2s — same beat as the success "Done" flash so both
    // states feel symmetric to the user.
    setTimeout(() => {
      setRegeneratingMap((prev) => {
        const e = prev[chatId]?.[variable];
        if (!e || e.abortedAt !== abortedAt) return prev;
        const next = { ...prev, [chatId]: { ...prev[chatId] } };
        delete next[chatId][variable];
        if (Object.keys(next[chatId]).length === 0) delete next[chatId];
        return next;
      });
    }, 2000);
  };

  const stopRegenerating = (chatId, variable) => {
    setRegeneratingMap((prev) => {
      if (!prev[chatId]?.[variable]) return prev;
      const next = { ...prev, [chatId]: { ...prev[chatId] } };
      delete next[chatId][variable];
      if (Object.keys(next[chatId]).length === 0) delete next[chatId];
      return next;
    });
  };

  const stopAllRegeneratingForChat = (chatId) => {
    setRegeneratingMap((prev) => {
      if (!prev[chatId]) return prev;
      const next = { ...prev };
      delete next[chatId];
      return next;
    });
  };

  // Same as stopAllRegeneratingForChat but only clears entries matching `mode`.
  // Used to give regenerate vs voice-edit different completion-flash durations
  // (regen: 500ms — user wants content fast; voice-edit: ~2s — body content
  // already visible, longer "Done" beat feels more satisfying).
  const stopRegeneratingByMode = (chatId, mode) => {
    setRegeneratingMap((prev) => {
      if (!prev[chatId]) return prev;
      const chatEntries = prev[chatId];
      const remaining = {};
      for (const v of Object.keys(chatEntries)) {
        if (chatEntries[v]?.mode !== mode) remaining[v] = chatEntries[v];
      }
      const next = { ...prev };
      if (Object.keys(remaining).length === 0) {
        delete next[chatId];
      } else {
        next[chatId] = remaining;
      }
      return next;
    });
  };

  // Marks every variable currently regenerating for this chat as "completing".
  // Used the moment refreshChat returns with new content from the SSE event:
  // we want a brief (~500ms) celebration animation — bar zooms to 100% with a
  // glow flash — before unmounting the skeleton and revealing the real content.
  const markAllRegeneratingCompleteForChat = (chatId) => {
    setRegeneratingMap((prev) => {
      if (!prev[chatId]) return prev;
      const completedAt = Date.now();
      const updated = {};
      for (const v of Object.keys(prev[chatId])) {
        updated[v] = { ...prev[chatId][v], completing: true, completedAt };
      }
      return { ...prev, [chatId]: updated };
    });
  };

  // Per-entry completion: marks complete (and schedules unmount) the sections
  // whose contextVariable has actually changed vs. their captured beforeContent.
  // Parallel regens on different sections are independent — Section A finishing
  // doesn't terminate Section B if B's content hasn't changed yet.
  //
  // options.markOldestIfNoneChanged: when set AND no entry's content changed,
  // mark the OLDEST non-completing entry complete as a best-guess. Use this
  // from positive completion signals (SSE generationCompleted, recovery
  // checks) — handles regens that produce identical content (common for short
  // sections like Allergies / Past Surgical History where the content-diff
  // check can't tell completion from "still running"). FIFO heuristic: SSE
  // most likely corresponds to the oldest pending regen.
  const markRegeneratingCompleteIfChanged = (chatId, contextVariables, options = {}) => {
    const current = regeneratingMapRef.current[chatId];
    if (!current) return;

    const { markOldestIfNoneChanged, forceCompleteVariable } = options;

    // forceCompleteVariable: caller knows exactly which variable just finished
    // (e.g., voice-edit AI Update Note returned a response targeted at one
    // section). Skip the content-diff check — voice-edit doesn't apply the
    // change until the user confirms in the modal, so beforeContent is
    // unchanged at this point but we still want the bar to flash "Done."
    if (forceCompleteVariable) {
      const entry = current[forceCompleteVariable];
      if (!entry || entry.completing || entry.aborted) return;
      const completedAt = Date.now();
      setRegeneratingMap((prev) => {
        if (!prev[chatId]?.[forceCompleteVariable]) return prev;
        const updated = { ...prev[chatId] };
        updated[forceCompleteVariable] = { ...updated[forceCompleteVariable], completing: true, completedAt };
        return { ...prev, [chatId]: updated };
      });
      const delay = entry.mode === "voiceEdit" ? 2000 : 500;
      setTimeout(() => {
        setRegeneratingMap((prev) => {
          const e = prev[chatId]?.[forceCompleteVariable];
          if (!e || e.completedAt !== completedAt) return prev;
          const next = { ...prev, [chatId]: { ...prev[chatId] } };
          delete next[chatId][forceCompleteVariable];
          if (Object.keys(next[chatId]).length === 0) delete next[chatId];
          return next;
        });
      }, delay);
      return;
    }

    if (!contextVariables) return;
    const justCompleted = [];
    const ambiguousNonCompleting = []; // identical content, can't disambiguate

    for (const v of Object.keys(current)) {
      const entry = current[v];
      if (entry.completing || entry.aborted) continue; // already terminal, skip
      const newContent = contextVariables["local:" + v];
      if (newContent !== entry.beforeContent) {
        justCompleted.push({ variable: v, mode: entry.mode });
      } else {
        ambiguousNonCompleting.push({ variable: v, mode: entry.mode, startedAt: entry.startedAt });
      }
    }

    // Fallback: SSE fired but nothing visibly changed. Only mark complete if
    // there's exactly ONE pending entry with identical content — multiple
    // ambiguous entries can't be safely disambiguated without backend info,
    // so we leave them for the recovery timer (30s/60s) or hard abort (90s).
    // This eliminates the false-positive where an unrelated SSE marks the
    // wrong section complete.
    if (justCompleted.length === 0 && markOldestIfNoneChanged && ambiguousNonCompleting.length === 1) {
      justCompleted.push(ambiguousNonCompleting[0]);
    }

    if (justCompleted.length === 0) return;

    const completedAt = Date.now();
    setRegeneratingMap((prev) => {
      if (!prev[chatId]) return prev;
      const updated = { ...prev[chatId] };
      for (const { variable } of justCompleted) {
        if (updated[variable] && !updated[variable].completing) {
          updated[variable] = { ...updated[variable], completing: true, completedAt };
        }
      }
      return { ...prev, [chatId]: updated };
    });

    // Schedule each entry's unmount independently. Race protection: if the
    // user re-starts the same regen during the completing window, the new
    // entry will have a different completedAt (or none), and the timeout
    // becomes a no-op.
    for (const { variable, mode } of justCompleted) {
      const delay = mode === "voiceEdit" ? 2000 : 500;
      setTimeout(() => {
        setRegeneratingMap((prev) => {
          const entry = prev[chatId]?.[variable];
          if (!entry || entry.completedAt !== completedAt) return prev;
          const next = { ...prev, [chatId]: { ...prev[chatId] } };
          delete next[chatId][variable];
          if (Object.keys(next[chatId]).length === 0) delete next[chatId];
          return next;
        });
      }, delay);
    }
  };

  const setGenerating = (chatId, value) => {
    if (value) {
      generatingMap[chatId] = value;
      // Full-note generation recovery. The browser flag is normally cleared by
      // the "assessment" SSE. If that completion push is missed or never sent
      // (SSE drop, a job too fast to be polled, or a backend section error), the
      // note finishes but the flag stays stuck -- locking the note and pinning
      // the spinner. Mirror the section-regen recovery (startRegenerating):
      // schedule one-shot checks that ask useChats to refetch and clear the flag
      // once the note is actually done, plus a hard backstop at 3 min so a note
      // can never stay locked indefinitely on a missed push.
      if (!generatingStartRef.current[chatId]) {
        const startedAt = Date.now();
        generatingStartRef.current[chatId] = startedAt;
        const scheduleRecovery = (delay, force) => {
          setTimeout(() => {
            // Superseded by a newer generation, or already cleared normally.
            if (generatingStartRef.current[chatId] !== startedAt) return;
            publish("generationRecoveryCheck", { chatId, force });
          }, delay);
        };
        scheduleRecovery(45000, false);
        scheduleRecovery(90000, false);
        scheduleRecovery(180000, true);
      }
    } else {
      delete generatingMap[chatId];
      delete generatingStartRef.current[chatId];
    }
    setGeneratingMap({ ...generatingMap });

    // Persist to localStorage so spinner survives hard refresh
    try {
      let stored = {};
      for (let id of Object.keys(generatingMap)) {
        stored[id] = { userId: generatingMap[id], ts: Date.now() };
      }
      if (Object.keys(stored).length > 0) {
        localStorage.setItem("generatingMap", JSON.stringify(stored));
      } else {
        localStorage.removeItem("generatingMap");
      }
    } catch (e) {
      // ignore
    }
  };

  const myJobs = useMemo(() => {
    let myJobs = Object.keys(generatingMap).reduce((prev, id) => {
      if (generatingMap[id] === auth?.user?.id) {
        prev[id] = generatingMap[id];
      }
      return prev;
    }, {});
    return myJobs;
  }, [generatingMap, auth?.user?.id]);

  return {
    jobs,
    jobsMap,
    generatingMap,
    myJobs,
    hasJobType,
    isGenerating,
    setGenerating,
    isRegenerating,
    getRegenerating,
    hasAnyRegenerating,
    startRegenerating,
    stopRegenerating,
    stopAllRegeneratingForChat,
    stopRegeneratingByMode,
    markAllRegeneratingCompleteForChat,
    markRegeneratingCompleteIfChanged,
    markRegeneratingAborted,
    userActions,
    getPing,
  };
}

export function ProvideJob({ children }) {
  const values = useJobProvider();
  return <jobContext.Provider value={values}>{children}</jobContext.Provider>;
}

export const useJob = () => {
  return useContext(jobContext);
};
